import { Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import React from "react";

import { UserContext } from "../../../Context/UserContext";
import { DateTime } from "../../../Types/DateTime";
import { Error } from "../../../Utils/Errors";
import { loadFromLocal, saveToLocal } from "../../../Utils/LocalStorage";
import { useApi } from "../../../Utils/useApi";

const noDataString = "No Data";
const dayStartString = DateTime.now().startOf("day").toISO();
const dayEndString = DateTime.now().endOf("day").toISO();

const LiveNewLeadRate: React.FC = () => {
  const {
    user,
    signalR: {
      payloads: { newLeadRateUpdates },
      subscribe
    }
  } = useContext(UserContext);
  const [timestamp, setTimestamp] = useState(() => {
    const prev = loadFromLocal<string>("leadRateTimestamp");
    if (!prev || DateTime.fromISO(prev).diff(DateTime.now(), "minutes").minutes > 5) {
      const now = DateTime.now().toISO();
      saveToLocal(now, "leadRateTimestamp");
      return now;
    }
    return undefined;
  });
  const [userRate, setUserRate] = useState(loadFromLocal<number>("userLeadRate"));
  const [orgRate, setOrgRate] = useState(loadFromLocal<number>("orgLeadRate"));

  useEffect(() => subscribe(["newLeadRateUpdates"]), [subscribe]);

  // when new lead rates signal is broadcast, update triggering timestamp if none or > 5 mins old
  useEffect(() => {
    if (newLeadRateUpdates)
      setTimestamp(timestamp => {
        const now = DateTime.now();
        if (!timestamp || now.diff(DateTime.fromISO(timestamp), "minutes").minutes > 5) {
          saveToLocal(now.toISO(), "leadRateTimestamp");
          return now.toISO();
        }
        return timestamp;
      });
  }, [newLeadRateUpdates]);

  // get user and organization rates
  const { response: userResponse } = useApi<Record<string, number | null>>({
    url: "Legacy/V0/Reports/NewLeadRate",
    config: {
      headers: { timestamp: timestamp || "" },
      params: { userId: user.id, startDate: dayStartString, endDate: dayEndString }
    },
    valid: !!timestamp,
    error: Error.GetUserNewLeadRate
  });
  useEffect(() => {
    if (userResponse) {
      const newUserRate = userResponse[dayStartString];
      if (newUserRate) {
        setUserRate(newUserRate * 100);
        saveToLocal(newUserRate, "userLeadRate");
      }
    }
  }, [userResponse]);
  const { response: orgResponse } = useApi<Record<string, number | null>>({
    url: "Legacy/V0/Reports/NewLeadRate",
    config: {
      headers: { timestamp: timestamp || "" },
      params: { startDate: dayStartString, endDate: dayEndString }
    },
    valid: !!timestamp,
    error: Error.GetOrgNewLeadRate
  });
  useEffect(() => {
    if (orgResponse) {
      const newOrgRate = orgResponse[dayStartString];
      if (newOrgRate) {
        saveToLocal(newOrgRate, "orgLeadRate");
        setOrgRate(newOrgRate * 100);
      }
    }
  }, [orgResponse]);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2">NLR:</Typography>
      <Stack>
        <Tooltip title="My Lead Rate">
          <Typography
            variant="subtitle2"
            align="center"
            color={userRate && orgRate && userRate < orgRate ? "error" : undefined}
          >
            {userRate ? `${userRate.toFixed(2)}%` : noDataString}
          </Typography>
        </Tooltip>
        <Divider sx={theme => ({ backgroundColor: theme.palette.primary.contrastText })} />
        <Tooltip title="PAC Lead Rate">
          <Typography variant="subtitle2" align="center">
            {orgRate ? `${orgRate.toFixed(2)}%` : noDataString}
          </Typography>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default LiveNewLeadRate;
